import { NotificationType, useApp, useTranslation, useUserInfo, useUserSettings } from "@next-platform/client-common-core";
import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import { Spinner } from "@nextui-org/react";
import { useCallback, useEffect, useState } from "react";

import { getOfflineNotificationMessage } from "@/components/common/Notification/utils/notification";
import PageStaticMetaTags from "@/components/common/PageStaticMetaTags";
import { Text } from "@/components/customComponents";
import useShouldFilterNsfwCharacters from "@/components/search/hooks/useShouldFilterNsfwCharacters";
import SearchClientCharacterListing from "@/components/search/SearchClientCharacterListing";
import Trans from "@/components/Trans/Trans";
import {
  DEFAULT_RECOMMENDED_PAGE_RECOMMENDATIONS_NUM,
  NSFW_LOCAL_STORAGE_KEY,
  NSFW_TOGGLE_LOCALSTORAGE_KEY,
  RECOMMENDED_PAGE_RECOMMENDATIONS_LOCAL_STORAGE_KEY_PREFIX,
  SFW_LOCAL_STORAGE_KEY,
} from "@/constants";
import { IRecommendationValues } from "@/hooks/types";
import { useRecombee } from "@/hooks/useRecombee";
import useToast from "@/hooks/useToast";
import useAuth from "@/services/useAuth";
import { typedLocalStorageService } from "@/utils/localStorageRe";

const ChatbotRecommended = () => {
  const { getCachedRecommendations } = useRecombee();
  const { t } = useTranslation();
  const { recombeeRecommendationsConfig, isNsfwEnabled } = useApp();
  const applicationId = basicAppConfig.applicationId;
  const { recommendationspageFetchCount } = recombeeRecommendationsConfig ?? {};
  const { displayToast } = useToast();
  const [charHits, setCharHits] = useState<IRecommendationValues[] | undefined>(undefined);
  const [recommId, setRecommId] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const { isLoading: isAuthLoading } = useAuth() || {};
  const { blockedUsers, selectedTags: selectedTagsStore, showNSFW } = useUserSettings();
  const nsfwToggleStorage = isNsfwEnabled && typedLocalStorageService.getItem(NSFW_TOGGLE_LOCALSTORAGE_KEY);
  const [isNsfwToggle, setIsNsfwToggle] = useState(nsfwToggleStorage === "true");
  const [hasRefetchingRecommendations, setHasRefetchingRecommendations] = useState(false);
  const { isEffectiveNsfwToggleEnabled } = useShouldFilterNsfwCharacters({
    isNsfwToggled: isNsfwToggle,
    isShowNsfwEnabled: showNSFW,
  });

  const { userId, isLoading: isUserLoading } = useUserInfo();
  const scenario = recombeeRecommendationsConfig?.navigateScenarioName;
  const cacheTimeout = recombeeRecommendationsConfig?.navigateCacheTimeoutMs;
  const getRecommendationsForUser = useCallback(async () => {
    setHasRefetchingRecommendations(true);
    const storageKey = `${RECOMMENDED_PAGE_RECOMMENDATIONS_LOCAL_STORAGE_KEY_PREFIX}_${
      isEffectiveNsfwToggleEnabled ? NSFW_LOCAL_STORAGE_KEY : SFW_LOCAL_STORAGE_KEY
    }`;
    const recommendations = await getCachedRecommendations({
      storageKey,
      timeOut: cacheTimeout,
      userId: userId || "",
      scenario: scenario || undefined,
      recommendationsCount: recommendationspageFetchCount ?? DEFAULT_RECOMMENDED_PAGE_RECOMMENDATIONS_NUM,
      nsfwToggle: isEffectiveNsfwToggleEnabled,
      blockedUsers,
      selectedTags: selectedTagsStore,
      applicationId: applicationId || null,
    });
    setRecommId(recommendations?.recommId || "");
    setCharHits(recommendations?.recomms);
    setIsLoading(false);
    setHasRefetchingRecommendations(false);
    return recommendations;
  }, [
    isEffectiveNsfwToggleEnabled,
    scenario,
    cacheTimeout,
    selectedTagsStore?.length,
    applicationId,
    blockedUsers?.length,
    recommendationspageFetchCount,
  ]);
  // if location is homepage only and no search query,no tags show recommendations

  useEffect(() => {
    if (!navigator.onLine) {
      displayToast({ message: getOfflineNotificationMessage(t), type: NotificationType.warning });
    }
    if (userId && scenario && !isAuthLoading && !isUserLoading) {
      void getRecommendationsForUser();
    }
  }, [displayToast, getRecommendationsForUser, isAuthLoading, isUserLoading, scenario, t, userId]);

  const handleRefresh = useCallback(async () => {
    const suffix = isEffectiveNsfwToggleEnabled ? NSFW_LOCAL_STORAGE_KEY : SFW_LOCAL_STORAGE_KEY;
    const storageKey = `${RECOMMENDED_PAGE_RECOMMENDATIONS_LOCAL_STORAGE_KEY_PREFIX}_${suffix}`;
    let existingRecommItems = null;
    try {
      const existingRecommItemsString = typedLocalStorageService.getItem(storageKey);
      if (existingRecommItemsString) {
        existingRecommItems = JSON.parse(existingRecommItemsString);
      }
    } catch {
      existingRecommItems = null;
    }
    void typedLocalStorageService.removeItem(storageKey);
    const newRecommendations = await getRecommendationsForUser();
    if (existingRecommItems && newRecommendations) {
      const existingRecommendationIds = new Set(
        existingRecommItems.recomms.map((existingRecommendation: IRecommendationValues) => existingRecommendation.id),
      );
      const isAnyNewRecommentdation = newRecommendations.recomms?.some((recommendation) => !existingRecommendationIds.has(recommendation.id));
      if (!isAnyNewRecommentdation) {
        displayToast({ message: t("chatbot:toast.success.noNewRecommendations"), type: NotificationType.success });
      }
    }
  }, [isEffectiveNsfwToggleEnabled, getRecommendationsForUser, displayToast, t]);

  if (isLoading) {
    return <Spinner />;
  }
  if (!charHits || charHits.length === 0) {
    return null;
  }

  const recommendedBotsIds = charHits.reduce((prev: string[], current) => [...prev, current.id], []);
  return (
    <>
      <PageStaticMetaTags pageId="recommendedBots" />
      <Text h1 className="m-0 px-4 max-mob:px-2" variant="xxl">
        <Trans i18nKey="chatbot:page.recommended.title" />
      </Text>
      <SearchClientCharacterListing
        charactersList={recommendedBotsIds}
        recommId={recommId}
        useStoreTagFilters={true}
        onRefresh={handleRefresh}
        onNsfwToggle={setIsNsfwToggle}
        refetchingRecommendations={hasRefetchingRecommendations}
      />
    </>
  );
};

export default ChatbotRecommended;
